import type { VoipRoomData } from '../model/voip-room-store/interfaces'

import { useCsRoutes } from '@shared/api'

import { useVoipRoomStore } from '../model/voip-room-store'

export interface RoomTokenResult {
  success: boolean
  notReady?: boolean
  superseded?: boolean
  error?: { message: string }
}

interface RoomTokenApiReturn {
  fetchRoomToken: (bookingId: number) => Promise<RoomTokenResult>
}

// Booking→booking navigation can leave an older token request in flight; only the
// most recent request may write the shared voip-room store.
let activeRequestId = 0

export const useVoipRoomTokenApi = (): RoomTokenApiReturn => {
  const { $axios } = useNuxtApp()
  const voipRoomStore = useVoipRoomStore()

  const fetchRoomToken = async (bookingId: number): Promise<RoomTokenResult> => {
    const requestId = ++activeRequestId

    const url = useCsRoutes('booking.room-token', {
      '[booking_id]': bookingId,
    })

    if (!url) {
      voipRoomStore.setRoomData(null)
      voipRoomStore.setRoomLoading(false)
      console.warn('missing url for voip-room/room-token.hook.ts')
      return { success: false, error: { message: 'Missing room token url' } }
    }

    try {
      voipRoomStore.setRoomLoading(true)
      voipRoomStore.setRoomData(null)
      const { data } = await $axios.post(url)

      if (requestId !== activeRequestId) {
        return { success: false, superseded: true }
      }

      if (data?.success && data?.data) {
        voipRoomStore.setRoomData(data.data as VoipRoomData)

        return { success: true }
      }

      return { success: false, error: { message: data?.message ?? 'Unexpected room token response' } }
    } catch (err: any) {
      if (requestId !== activeRequestId) {
        return { success: false, superseded: true }
      }

      if (err?.response?.status === 409) {
        return { success: false, notReady: true }
      }

      console.error('unable to retrieve voip-room/room-token.hook.ts:', err)

      return { success: false, error: { message: err.message ?? 'Something went wrong' } }
    } finally {
      if (requestId === activeRequestId) {
        voipRoomStore.setRoomLoading(false)
      }
    }
  }

  return {
    fetchRoomToken,
  }
}
